import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    Typography,
    CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import NewTicketForm from './new-ticket.form';
import TicketFaq from './ticket-faq';
import { Styled } from 'components/dialog/dialog.styled';
import { immediateToast } from 'utils/toast';

type NewTicketProps = {
    variant?: 'button' | 'menuItem';
    routing?: string;
    buttonSx?: any;
};

type TicketDialogStep = 'faq' | 'form';

const NewTicket = ({ routing, buttonSx }: NewTicketProps) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState<TicketDialogStep>('faq');
    const [isCreatingTicket, setIsCreatingTicket] = useState(false);
    const handleOpen = () => {
        setStep('faq');
        setIsCreatingTicket(false);
        setOpen(true);
    };

    const handleClose = (force = false) => {
        if (isCreatingTicket && !force) return;
        setOpen(false);
        setStep('faq');
        setIsCreatingTicket(false);
    };

    const handleTicketCreated = (ticket: any) => {
        handleClose(true);

        immediateToast('success', {
            message: 'تیکت جدید با موفقیت ایجاد شد.',
            timeout: 3000,
        });

        navigate(routing ? `${routing}/${ticket?.id}` : `/ticket/${ticket?.id}`);
    };

    const isFaqStep = step === 'faq';

    return (
        <Grid item sx={{ pt: '0px !important' }}>
            <Button variant="contained" onClick={handleOpen} sx={buttonSx}>
                تیکت جدید
            </Button>

            <Dialog
                fullWidth
                maxWidth={false}
                onClose={() => undefined}
                disableEscapeKeyDown
                open={open}
                sx={(theme) => ({
                    '& .MuiDialog-container': {
                        alignItems: { xs: 'flex-end', sm: 'center' },
                    },
                    '& .MuiDialog-paper': {
                        width: { xs: '100%', sm: 720 },
                        maxWidth: { xs: '100%', sm: 'calc(100% - 32px)' },
                        height: {
                            xs: isFaqStep ? '92dvh' : 'auto',
                            sm: isFaqStep ? 'min(760px, 88vh)' : 'auto',
                        },
                        maxHeight: { xs: '92dvh', sm: '88vh' },
                        m: { xs: 0, sm: 2 },
                        borderRadius: {
                            xs: '18px 18px 0 0',
                            sm: '18px',
                        },
                        overflow: 'hidden',
                        position: 'relative',
                        backgroundImage: 'none',
                        backgroundColor: theme.palette.background.paper,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        boxShadow:
                            theme.palette.mode === 'dark'
                                ? '0 24px 70px rgba(0,0,0,.52)'
                                : '0 24px 70px rgba(18,24,40,.16)',
                    },
                })}>
                <Box
                    sx={(theme) => ({
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: { xs: 2, sm: 2.5 },
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: theme.palette.divider,
                        backgroundColor:
                            theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.paper, 0.98)
                                : theme.palette.background.paper,
                    })}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            gap: 1.25,
                        }}>
                        <Styled.Icon
                            sx={(theme) => ({
                                color:
                                    theme.palette.mode === 'dark'
                                        ? theme.palette.primary.light
                                        : theme.palette.primary.main,
                                backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'dark' ? 0.12 : 0.08,
                                ),
                                borderColor: alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'dark' ? 0.18 : 0.12,
                                ),
                            })}>
                            <i
                                className={
                                    isFaqStep
                                        ? 'fa-light fa-headset'
                                        : 'fa-light fa-message-plus'
                                }
                            />
                        </Styled.Icon>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    color: 'text.primary',
                                    fontFamily: 'YekanBalkhHeavy',
                                    fontSize: 16,
                                    lineHeight: 1.8,
                                }}>
                                {isFaqStep
                                    ? 'راهنما و سوالات پرتکرار'
                                    : 'ایجاد تیکت جدید'}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}>
                                {isFaqStep
                                    ? 'می‌توانید پاسخ خود را از این صفحه دریافت کنید.'
                                    : 'جزئیات درخواستت را کامل وارد کن تا سریع‌تر بررسی شود.'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {!isFaqStep && (
                            <Button
                                size="small"
                                onClick={() => setStep('faq')}
                                startIcon={
                                    <i className="fa-light fa-arrow-right" />
                                }
                                sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                    color: 'text.secondary',
                                    minWidth: 'auto',
                                    px: 1,
                                }}>
                                سوالات پرتکرار
                            </Button>
                        )}

                        <IconButton
                            onClick={() => handleClose()}
                            sx={(theme) => ({
                                width: 36,
                                height: 36,
                                color: 'text.secondary',
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                backgroundColor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.03)
                                        : alpha(theme.palette.common.black, 0.018),
                                '&:hover': {
                                    color: 'text.primary',
                                    backgroundColor: theme.palette.action.hover,
                                },
                            })}>
                            <i className="fa-light fa-xmark" />
                        </IconButton>
                    </Box>
                </Box>

                {isFaqStep ? (
                    <Box sx={{ flex: 1, minHeight: 0, pt: 1.5 }}>
                        <TicketFaq onContinue={() => setStep('form')} />
                    </Box>
                ) : (
                    <DialogContent
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            overflowY: 'auto',
                            '&:first-of-type': { pt: { xs: 2, sm: 2.5 } },
                        }}>
                        <Box
                            sx={(theme) => ({
                                display: { xs: 'flex', sm: 'none' },
                                mb: 1.5,
                                pb: 1.5,
                                borderBottom: '1px solid',
                                borderColor: theme.palette.divider,
                            })}>
                            <Button
                                size="small"
                                onClick={() => setStep('faq')}
                                startIcon={
                                    <i className="fa-light fa-arrow-right" />
                                }
                                sx={{ color: 'text.secondary', px: 0 }}>
                                بازگشت به سوالات پرتکرار
                            </Button>
                        </Box>

                        <NewTicketForm
                            routing={routing}
                            onClose={() => handleClose(true)}
                            onCreated={handleTicketCreated}
                            onLoadingChange={setIsCreatingTicket}
                        />
                    </DialogContent>
                )}

                {isCreatingTicket && (
                    <Box
                        sx={(theme) => ({
                            position: 'absolute',
                            inset: 0,
                            zIndex: 30,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 3,
                            backgroundColor:
                                theme.palette.mode === 'dark'
                                    ? 'rgba(10, 12, 18, 0.78)'
                                    : 'rgba(255, 255, 255, 0.78)',
                            backdropFilter: 'blur(4px)',
                        })}>
                        <Box
                            sx={(theme) => ({
                                minWidth: { xs: 'min(280px, 100%)', sm: 310 },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.25,
                                px: 3,
                                py: 2.5,
                                borderRadius: '16px',
                                border: '1px solid',
                                borderColor: theme.palette.divider,
                                backgroundColor: theme.palette.background.paper,
                                boxShadow:
                                    theme.palette.mode === 'dark'
                                        ? '0 18px 45px rgba(0,0,0,.45)'
                                        : '0 18px 45px rgba(18,24,40,.14)',
                            })}>
                            <CircularProgress size={34} thickness={4} />
                            <Typography
                                sx={{
                                    color: 'text.primary',
                                    fontFamily: 'YekanBalkhHeavy',
                                    fontSize: 15,
                                }}>
                                در حال ثبت درخواست پشتیبانی...
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                لطفاً تا تکمیل ثبت تیکت صبر کنید.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Dialog>
        </Grid>
    );
};

export default NewTicket;
